import { ChevronDown } from 'lucide-react'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu'
import { cn } from '@/utils'
import { FIELD_AT_REST, FIELD_EMPTY } from './fieldBox'

interface InlinePickOption {
  key: string
  label: string
}

interface InlinePickProps {
  value: string
  options: InlinePickOption[]
  placeholder: string
  title: string
  className?: string
  // Adds a choice that empties the value. Leave it out when a value is always required.
  clearLabel?: string
  onPick: (value: string) => void
}

// InlineText for a pick-one value: the chosen label as text, and the list opens from it.
export const InlinePick = ({
  value,
  options,
  placeholder,
  title,
  className,
  clearLabel,
  onPick,
}: InlinePickProps) => {
  const chosen = options.find((option) => option.key === value)
  const label = chosen?.label ?? placeholder
  const lastSpace = label.lastIndexOf(' ')
  const leadingWords = label.slice(0, lastSpace + 1)
  const lastWord = label.slice(lastSpace + 1)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        title={title}
        className={cn(
          FIELD_AT_REST,
          'outline-none focus-visible:ring-1 focus-visible:ring-neutral-900 data-[state=open]:bg-neutral-100',
          className,
          !chosen && FIELD_EMPTY,
        )}
      >
        <span className="min-w-0">
          {leadingWords}
          {/* The last word and the chevron never split, so the chevron cannot wrap alone. */}
          <span className="whitespace-nowrap">
            {lastWord}
            <ChevronDown className="ml-1.5 inline size-3.5 align-[-0.15em] text-ink-subtle opacity-50 transition-opacity group-hover/field:opacity-100 group-data-[state=open]/field:opacity-100" />
          </span>
        </span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-40">
        <DropdownMenuRadioGroup value={value} onValueChange={onPick}>
          {options.map((option) => (
            <DropdownMenuRadioItem key={option.key} value={option.key}>
              {option.label}
            </DropdownMenuRadioItem>
          ))}
          {clearLabel && (
            <DropdownMenuRadioItem value="" className="text-ink-subtle">
              {clearLabel}
            </DropdownMenuRadioItem>
          )}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
